import { daysUntilDeadline, isActiveBidStatus, isDeadlinePast } from "@/lib/bid-deadline"
import type { Bid } from "@/lib/schemas"

export const DEADLINE_WINDOW_DAYS = 60
/** Deadlines inside this many days read as "closing soon" (amber). */
export const DUE_SOON_DAYS = 14

export interface DeadlineRow {
  bid: Bid
  days: number
}

/**
 * The single deadline filter for Home: active (pre-submission), non-archived
 * bids with a parseable deadline, split into overdue and the next
 * `DEADLINE_WINDOW_DAYS` days, each sorted soonest first. The glance tiles
 * count from this same result so the numbers and the list never disagree.
 * Overdue is `isDeadlinePast`: a closing time that passed earlier today is
 * overdue (its `days` is still 0), a date-only deadline once its day ends.
 */
export function partitionDeadlines(
  bids: Bid[],
  now: Date = new Date(),
): { overdue: DeadlineRow[]; upcoming: DeadlineRow[] } {
  const overdue: DeadlineRow[] = []
  const upcoming: DeadlineRow[] = []

  for (const bid of bids) {
    if (!bid.deadline || bid.archived || !isActiveBidStatus(bid.status)) continue
    const days = daysUntilDeadline(bid.deadline, now)
    if (days === null) continue
    if (isDeadlinePast(bid, now)) overdue.push({ bid, days })
    else if (days <= DEADLINE_WINDOW_DAYS) upcoming.push({ bid, days })
  }

  // Soonest first; within one day, by the moment itself (a 9:00 am close
  // before a 2:00 pm one).
  const byDeadline = (a: DeadlineRow, b: DeadlineRow) =>
    a.days - b.days || new Date(a.bid.deadline ?? "").getTime() - new Date(b.bid.deadline ?? "").getTime()
  overdue.sort(byDeadline)
  upcoming.sort(byDeadline)
  return { overdue, upcoming }
}
